import type { TranslationKey } from '../i18n/locale';
/**
 * Mirrors server/src/domain/enums.ts for values the UI must render or compare.
 *
 * Duplicated rather than imported because the web app is a separate build with
 * no path into the server package. `enums.test.ts` on the server asserts this
 * file stays in step, so the copy cannot silently drift.
 */

export const UserRole = {
  Owner: 'owner',
  Admin: 'admin',
  Supervisor: 'supervisor',
  Staff: 'staff',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

/** Roles allowed to manage the Accurate connection and team. */
export const ADMIN_ROLES: readonly UserRole[] = [UserRole.Owner, UserRole.Admin];

/**
 * Roles allowed to void a posted receipt — supervisor and above, matching the
 * server's requireRole('supervisor'). Undoing a filed receipt is a correction
 * to the books, not part of the scanning job.
 *
 * Listed rather than ranked because the server owns the real check; this only
 * decides whether to render the button, and a list is harder to get subtly
 * wrong than a comparison.
 */
/**
 * What each role is called on screen, and what it actually permits.
 *
 * Record over UserRole, so adding a role without naming it is a compile error
 * rather than a raw 'supervisor' appearing in a dropdown.
 */
/*
 * Translation keys rather than text, so a label reads in whichever language
 * the user picked. The maps stay here because what they are keyed by — the
 * enum — belongs here; only the words moved.
 */
export const ROLE_LABEL: Record<UserRole, TranslationKey> = {
  [UserRole.Owner]: 'role.owner',
  [UserRole.Admin]: 'role.admin',
  [UserRole.Supervisor]: 'role.supervisor',
  [UserRole.Staff]: 'role.staff',
};

/**
 * The roles an admin may hand out. Owner is absent on purpose: a tenant keeps
 * exactly one, and moving it needs its own deliberate action.
 */
export const ASSIGNABLE_ROLE_LABEL: Record<
  Exclude<UserRole, typeof UserRole.Owner>,
  TranslationKey
> = {
  [UserRole.Admin]: 'role.adminDesc',
  [UserRole.Supervisor]: 'role.supervisorDesc',
  [UserRole.Staff]: 'role.staffDesc',
};

export const VOID_ROLES: readonly UserRole[] = [
  UserRole.Owner,
  UserRole.Admin,
  UserRole.Supervisor,
];

export const ReceiptStatus = {
  Pending: 'pending',
  Posted: 'posted',
  Failed: 'failed',
  Voided: 'voided',
} as const;
export type ReceiptStatus = (typeof ReceiptStatus)[keyof typeof ReceiptStatus];

export const SubscriptionStatus = {
  Trialing: 'trialing',
  Active: 'active',
  PastDue: 'past_due',
  Cancelled: 'cancelled',
  Expired: 'expired',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const InvoiceStatus = {
  Pending: 'pending',
  Paid: 'paid',
  Failed: 'failed',
  Expired: 'expired',
  Refunded: 'refunded',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

export const Fulfilment = {
  Complete: 'Sesuai',
  Short: 'Kurang',
} as const;
export type Fulfilment = (typeof Fulfilment)[keyof typeof Fulfilment];

/**
 * Indonesian labels and badge colours, keyed by status.
 *
 * Using Record<Status, …> rather than a lookup with a fallback means adding a
 * status to the enum without labelling it is a compile error, not an untranslated
 * raw value leaking into the UI.
 */
export const RECEIPT_STATUS_LABEL: Record<ReceiptStatus, { key: TranslationKey; cls: string }> = {
  [ReceiptStatus.Posted]: { key: 'receiptStatus.posted', cls: 'bg-emerald-600' },
  [ReceiptStatus.Pending]: { key: 'receiptStatus.pending', cls: 'bg-amber-500' },
  [ReceiptStatus.Failed]: { key: 'receiptStatus.failed', cls: 'bg-red-600' },
  [ReceiptStatus.Voided]: { key: 'receiptStatus.voided', cls: 'bg-slate-400' },
};

export const BILLING_STATUS_LABEL: Record<
  SubscriptionStatus | InvoiceStatus,
  { key: TranslationKey; cls: string }
> = {
  [SubscriptionStatus.Trialing]: { key: 'billingStatus.trialing', cls: 'text-blue-600' },
  [SubscriptionStatus.Active]: { key: 'billingStatus.active', cls: 'text-emerald-600' },
  [SubscriptionStatus.PastDue]: { key: 'billingStatus.pastDue', cls: 'text-red-600' },
  [SubscriptionStatus.Cancelled]: { key: 'billingStatus.cancelled', cls: 'text-slate-400' },
  [InvoiceStatus.Paid]: { key: 'billingStatus.paid', cls: 'text-emerald-600' },
  [InvoiceStatus.Pending]: { key: 'billingStatus.pending', cls: 'text-amber-600' },
  [InvoiceStatus.Failed]: { key: 'billingStatus.failed', cls: 'text-red-600' },
  [InvoiceStatus.Expired]: { key: 'billingStatus.expired', cls: 'text-slate-400' },
  [InvoiceStatus.Refunded]: { key: 'billingStatus.refunded', cls: 'text-slate-500' },
};
